import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useBranding } from '@/contexts/BrandingContext';
import { useAuth } from '@/contexts/AuthContext';
import { ROLES } from '@/lib/roles';
import { canAccessRoute } from '@/lib/permissions';
import {
  LayoutDashboard, QrCode, Tags, AlertTriangle, ShieldCheck, Settings, TrendingUp,
  Ticket, Users, ListChecks, Megaphone, Car, History, FileText, Bell, Cog,
  ChevronDown, ChevronRight, Map as MapIcon, LogOut, UserCog, X, Ban
} from 'lucide-react';

const isLightColor = (hex) => {
  const c = hex.replace('#', '');
  const r = parseInt(c.substring(0, 2), 16);
  const g = parseInt(c.substring(2, 4), 16);
  const b = parseInt(c.substring(4, 6), 16);
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.5;
};

export const Sidebar = ({ isOpen = false, onClose = () => {} }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { branding } = useBranding();
  const { user, logout } = useAuth();

  const role = user?.role || 'admin';

  const allMenuItems = [
    { label: 'Dashboard', icon: LayoutDashboard, path: '/' },
    { label: 'Tags Online', icon: Car, path: '/fleet' },
    { label: 'Ocorrências', icon: AlertTriangle, path: '/incidents' },
    { label: 'Tickets', icon: Ticket, path: '/tickets' },
    { label: 'Mapa da Frota', icon: MapIcon, path: '/map' },
    { label: 'Gestão de Tags', icon: QrCode, path: '/tags' },
    { label: 'Alertas em Massa', icon: Megaphone, path: '/mass-alerts' },
    { label: 'Tipos de Ocorrência', icon: ListChecks, path: '/incident-types' },
    { label: 'Histórico', icon: History, path: '/activity-log' },
    { label: 'Relatórios', icon: FileText, path: '/reports' },
    { label: 'Utilizadores', icon: Users, path: '/users' },
    { label: 'Contactos', icon: UserCog, path: '/external-contacts' },
  ];
  const menuItems = allMenuItems.filter(i => canAccessRoute(i.path, user?.permissions));

  const allConfigSubItems = [
    { label: 'Regras Tags com responsável', icon: Bell, path: '/driver-contact-rules' },
    { label: 'Regras Tags sem responsável', icon: Bell, path: '/contact-rules' },
    // 'Objetos' (tipos de objeto) movido para o portal Superadmin
    { label: 'Categorias', icon: Tags, path: '/categories' },
    { label: 'Niveis de Prioridade', icon: AlertTriangle, path: '/occurrence-priorities' },
    { label: 'Tipos Contactos', icon: UserCog, path: '/external-contact-types' },
    { label: 'Estados de Recursos', icon: ListChecks, path: '/resource-statuses' },
    { label: 'Motivos Desativacao Tags', icon: Ban, path: '/tag-deactivation-reasons' },
    { label: 'Modelos Alertas em Massa', icon: Megaphone, path: '/mass-alert-templates' },
    { label: 'Permissões', icon: ShieldCheck, path: '/permissions' },
    { label: 'Sistema', icon: Settings, path: '/settings' },
  ];
  const configSubItems = allConfigSubItems.filter(i => canAccessRoute(i.path, user?.permissions));

  const configPaths = configSubItems.map(s => s.path);
  const isActive = (path) => location.pathname === path;
  const inConfig = configPaths.includes(location.pathname);

  const [configOpen, setConfigOpen] = useState(inConfig);

  const sidebarBg = branding.sidebarColor || '#0f172a';
  const accentBg = branding.accentColor || '#2563eb';
  const light = isLightColor(sidebarBg);
  const borderColor = light ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.08)';
  const textColor = light ? '#1e293b' : '#ffffff';
  const subtextColor = light ? '#64748b' : '#94a3b8';
  const hoverBg = light ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.06)';
  const menuTextColor = light ? '#475569' : '#cbd5e1';

  const renderLink = (item, isSub = false) => {
    const Icon = item.icon;
    const active = isActive(item.path);
    return (
      <Link
        key={item.path}
        to={item.path}
        data-testid={`nav-${item.path}`}
        className={`flex items-center gap-3 px-3 py-2.5 rounded-md transition-all ${isSub ? 'pl-9 text-[13px]' : ''}`}
        style={active ? {
          backgroundColor: accentBg, color: '#ffffff',
          boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)'
        } : { color: menuTextColor }}
        onMouseEnter={(e) => {
          if (!active) {
            e.currentTarget.style.backgroundColor = hoverBg;
            e.currentTarget.style.color = textColor;
          }
        }}
        onMouseLeave={(e) => {
          if (!active) {
            e.currentTarget.style.backgroundColor = 'transparent';
            e.currentTarget.style.color = menuTextColor;
          }
        }}
      >
        <Icon size={isSub ? 16 : 20} strokeWidth={1.5} />
        <span className="font-primary font-medium text-sm flex-1">{item.label}</span>
        {item.badge ? (
          <span
            className="text-[10px] font-bold rounded-full px-1.5 py-0.5"
            style={{ backgroundColor: '#facc15', color: '#0c1a3a' }}
            data-testid={`sidebar-badge-${item.path}`}
          >
            {item.badge}
          </span>
        ) : null}
      </Link>
    );
  };

  return (
    <>
      {/* Mobile backdrop */}
      {isOpen && (
        <div
          className="md:hidden fixed inset-0 bg-black/50 z-30"
          onClick={onClose}
          data-testid="sidebar-backdrop"
          aria-hidden="true"
        />
      )}
      <div
        className={`w-64 h-screen fixed left-0 top-0 flex flex-col z-40 transition-transform duration-300 md:translate-x-0 ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        style={{ backgroundColor: sidebarBg }}
        data-testid="sidebar"
      >
      <div className="px-4 pt-5 pb-4 relative" style={{ borderBottom: `1px solid ${borderColor}` }}>
        {/* Mobile close button */}
        <button
          type="button"
          onClick={onClose}
          className="md:hidden absolute top-3 right-3 p-1.5 rounded-md hover:bg-white/10 transition-colors"
          style={{ color: textColor }}
          data-testid="sidebar-close"
          aria-label="Fechar menu"
        >
          <X size={18} />
        </button>
        {branding.logo ? (
          <div className="flex flex-col items-center gap-2">
            <img
              src={branding.logo}
              alt="Logo"
              className="w-full max-h-16 object-contain rounded-lg"
              style={{ backgroundColor: light ? 'transparent' : 'rgba(255,255,255,0.1)' }}
            />
            <p className="text-[11px] font-medium" style={{ color: subtextColor }}>
              {branding.companySubtitle || 'Gestão de Frotas'}
            </p>
          </div>
        ) : (
          <div className="flex items-center gap-3">
            <div className="rounded-lg p-2" style={{ backgroundColor: accentBg }}>
              <TrendingUp className="text-white" size={24} strokeWidth={2} />
            </div>
            <div>
              <h1 className="font-secondary font-bold text-lg" style={{ color: textColor }}>
                {branding.companyName || 'condutor.online'}
              </h1>
              <p className="text-xs" style={{ color: subtextColor }}>
                {branding.companySubtitle || 'Gestão de Frotas'}
              </p>
            </div>
          </div>
        )}
      </div>

      <nav className="flex-1 p-4 space-y-1 overflow-y-auto" data-testid="sidebar-nav">
        {menuItems.map(item => renderLink(item))}

        {/* Configurações group — apenas se utilizador tem acesso a algo */}
        {configSubItems.length > 0 && (
          <>
            <button
              type="button"
              data-testid="nav-config-toggle"
              onClick={() => setConfigOpen(o => !o)}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-md transition-all"
              style={{
                color: inConfig ? textColor : menuTextColor,
                backgroundColor: inConfig ? hoverBg : 'transparent',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = hoverBg; e.currentTarget.style.color = textColor; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = inConfig ? hoverBg : 'transparent'; e.currentTarget.style.color = inConfig ? textColor : menuTextColor; }}
            >
              <Cog size={20} strokeWidth={1.5} />
              <span className="font-primary font-medium text-sm flex-1 text-left">Configurações</span>
              {configOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            </button>
            {configOpen && (
              <div className="space-y-0.5 mt-0.5">
                {configSubItems.map(item => renderLink(item, true))}
              </div>
            )}
          </>
        )}
      </nav>

      <div className="p-4" style={{ borderTop: `1px solid ${borderColor}` }}>
        <div className="flex items-center gap-3 px-3 py-2">
          <div
            className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: light ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.12)' }}
          >
            <span className="text-sm font-medium" style={{ color: textColor }}>
              {(user?.name || 'AD').split(' ').map(n => n[0]).slice(0, 2).join('')}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate" style={{ color: textColor }}>{user?.name || 'Admin'}</p>
            <div className="flex items-center gap-1.5">
              <span
                className="inline-flex items-center text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded"
                style={{ backgroundColor: accentBg + '30', color: accentBg }}
                data-testid="user-role-badge"
              >
                {ROLES[role]?.label || role}
              </span>
              <p className="text-xs truncate" style={{ color: subtextColor }}>{user?.email || ''}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => navigate('/account')}
            data-testid="account-btn"
            className="p-1.5 rounded-md hover:bg-white/10 transition-colors flex-shrink-0"
            title="A minha conta"
            style={{ color: subtextColor }}
          >
            <UserCog size={16} />
          </button>
          <button
            type="button"
            onClick={() => { logout(); navigate('/login'); }}
            data-testid="logout-btn"
            className="p-1.5 rounded-md hover:bg-white/10 transition-colors flex-shrink-0"
            title="Terminar sessão"
            style={{ color: subtextColor }}
          >
            <LogOut size={16} />
          </button>
        </div>
        <div className="flex items-center justify-center gap-1.5 mt-2 text-[9px]" style={{ color: subtextColor }}>
          <span>Powered by</span>
          <div className="bg-black rounded px-1.5 py-0.5">
            <img src="/branding/condutor-online-logo.png" alt="condutor.online" className="h-2.5 w-auto block" />
          </div>
        </div>
      </div>
    </div>
    </>
  );
};
